/**
 * บอสโลก, round 2: the mechanics beyond the plain shapes (docs/systems/WORLD_BOSSES.md section 2). Data only; the rules are
 * systems/worldBossMech.ts. Everything a fight needs from the land is a **site**: a disc placed relative to the arena
 * centre and addressed by id. The region lane will give the arenas real sites (a crag ledge, rocks, a spire's overhang);
 * until then these are placeholders and one row each moves them. Distances are metres.
 */
import type { MonsterDef } from './monsters';

export interface WbSite {
  /** Offset from the arena centre. */
  readonly dx: number;
  readonly dy: number;
  readonly r: number;
  /** Height above the ground for a ledge or a deck (the renderer places the boss or the mark there). */
  readonly z?: number;
}

export const WB_SITES: Readonly<Record<string, WbSite>> = {
  // Ape (jz-site-ape, arena 2160,2520 r90; ground 50-72 m): the ruin terrace (64 m, flat, 20 x 12 m at the east) ends in a 14 m
  // face on its east side. The ape sits on the terrace edge and falls to the foot of the face. (No 30 m crag exists in this arena.)
  'wb-ape:ledge': { dx: 74, dy: -38, r: 3, z: 14 },
  'wb-ape:foot': { dx: 80, dy: -32, r: 3 },
  // Bird (jz-site-firebird, arena 2700,836 r130; the summit plateau is 187-194 m): nests on the flat top; shelters at the
  // steepest slope lips round the summit (30-40 m out, 150-175 m high); thermal columns on the slopes further out.
  'wb-bird:nest-1': { dx: -20, dy: -15, r: 2 },
  'wb-bird:nest-2': { dx: -8, dy: -4, r: 2 },
  'wb-bird:nest-3': { dx: -12, dy: 6, r: 2 },
  'wb-bird:shelter-1': { dx: 0, dy: 30, r: 4 },
  'wb-bird:shelter-2': { dx: -38, dy: 0, r: 4 },
  'wb-bird:shelter-3': { dx: -20, dy: -35, r: 4 },
  'wb-bird:thermal-1': { dx: 27, dy: 47, r: 5 },
  'wb-bird:thermal-2': { dx: 0, dy: -54, r: 5 },
  // Turtle (jz-site-turtle, arena 1885,3311 r90; open bay, the boss spawns in water at dx -34): safe ground is the east beach
  // (rock-1..3, dry land 4-9 m high, rock-1 the nearest to the middle) and a 48 m sea stack (rock-4, climb it).  The rods float round the spawn.
  'wb-turtle:rock-1': { dx: 12, dy: -12, r: 3 },
  'wb-turtle:rock-2': { dx: 14, dy: -40, r: 3 },
  'wb-turtle:rock-3': { dx: 24, dy: 18, r: 3 },
  'wb-turtle:rock-4': { dx: -15, dy: 50, r: 6, z: 48 },
  'wb-turtle:deck': { dx: -23, dy: -20, r: 7, z: 0.75 },
  'wb-turtle:rod-1': { dx: -46, dy: -11, r: 1.5 },
  'wb-turtle:rod-2': { dx: -23, dy: -11, r: 1.5 },
  'wb-turtle:rod-3': { dx: -46, dy: 12, r: 1.5 },
  'wb-turtle:rod-4': { dx: -23, dy: 12, r: 1.5 },
};

/** Site ids of a boss by prefix: `sitesOf('wb-turtle:rock')`. */
export const sitesOf = (prefix: string): string[] => Object.keys(WB_SITES).filter((k) => k.startsWith(prefix));

/** The harbour direction a wave pushes toward (a unit vector, map coordinates). */
export const WB_HARBOUR = { dx: 0, dy: 1 } as const;

// ───────────────────────── ape ─────────────────────────

/**
 * Phase 2: it leaps to the ledge and throws a volley every `every` seconds (`volleyMove`). `breaks` landed ท่าทำลาย on it
 * bring it down, มึนงง `stun` seconds. Nobody breaks it in `max` seconds and it drops on its own (no stun). The
 * helpers (mode npc) land a break every `simEvery` seconds with the mechanic chance.
 */
export const WB_PERCH = { volleyMove: 'wb-ape-ledge', every: 6, max: 45, breaks: 3, stun: 6, simEvery: 6 } as const;

/** Mimic: it watches the hits of the last `window` seconds, then holds the stance that beats them for `stance` seconds. */
export const WB_MIMIC = { move: 'wb-ape-mimic', window: 10, stance: 6, bounce: 0.4 } as const;

/** A thrown boulder stays `seconds` as cover: it shields a player standing within `behind` m behind it. */
export const WB_COVER = { seconds: 30, r: 2, behind: 4, max: 6 } as const;

/** The chest-beat call: small apes, at most `max` alive. */
export const WB_YOUNG = { id: 'wb-ape-young', count: 4, max: 8, ring: 5 } as const;

// ───────────────────────── bird ─────────────────────────

/**
 * Phase 2 in the air: `alt` metres up. A hit reaches it from an attacker higher than `reach` (a ledge, a column, the top of a
 * glide) or from a ranged art (range at least `ranged`, damage x `rangedMul`); others do nothing. A dive lands it
 * `down` seconds, stunned, hit by all.
 */
export const WB_AIR = { alt: 30, reach: 18, ranged: 10, rangedMul: 0.8, down: 5 } as const;

/** Thermal columns (ลมยก made by the bird's fire): a glider inside rises toward `lift` m/s until `top` m above the boss's ground. */
export const WB_THERMAL = { top: 42, lift: 7, grip: 3 } as const;

/** Phase 3: eggs on the nest. One that hatches (the fuse runs out) heals it `heal` of its health. */
export const WB_EGG = {
  id: 'wb-fire-egg',
  layMove: 'wb-bird-eggs',
  count: 3,
  hp: 1500,
  fuse: 25,
  heal: 0.1,
  /** Seconds after the last egg is gone before it lays again. */
  again: 15,
  /** The helpers break one at this age with this chance. */
  simAt: 12,
  simBreak: 0.5,
} as const;

// ───────────────────────── turtle ─────────────────────────

/**
 * Phase 2: the shell closes for `seconds`, taking `taken` of the damage, and `rods` lightning rods rise (`rodHp` each).
 * All down: the shell cracks, มึนงง `crackStun`, and it takes x`crackTaken` for the rest of the phase. A bolt within
 * `draw` m of a rod strikes the rod for `bolt`. The helpers break a rod every `simEvery` seconds with the mechanic chance.
 */
export const WB_SHELL = {
  seconds: 20,
  rods: 4,
  rodId: 'wb-lightning-rod',
  rodHp: 800,
  taken: 0.1,
  crackStun: 6,
  crackTaken: 1.25,
  draw: 6,
  bolt: 400,
  simEvery: 5,
} as const;

/** Phase 3: a storm. The sea swell doubles (water lane) and `storm` is true in the state. */
export const WB_STORM = { swell: 2 } as const;

// ───────────────────────── ground patches ─────────────────────────

/** Fire (ไฟลุก) hits a second at `fire` power; smoke puts พิษ a second; both while standing in them. */
export const WB_PATCH = { every: 1, fire: 0.18, smokeStatus: 'poison' } as const;

// ───────────────────────── per move ─────────────────────────

/** What a move adds to the shape rule in `WB_MOVES` (kept apart so the dungeon's rule type stays as it is). */
export interface MoveMech {
  /** Never picked by the dice: the boss's script uses it. */
  readonly scheduled?: boolean;
  /** Not used while the bird is aloft. */
  readonly groundOnly?: boolean;
  /** Rings that follow one another as bands (outer radii), `gap` seconds apart. */
  readonly rings?: readonly number[];
  readonly gap?: number;
  /** Thunder: everyone in water takes double (power and stun). */
  readonly thunder?: boolean;
  /** Sites that avoid it for whoever stands in one (prefixes of `WB_SITES`), and whether they are drawn marked. */
  readonly safe?: { readonly sites: readonly string[]; readonly marked?: boolean };
  /** A boulder in front of the blow shields. */
  readonly cover?: boolean;
  /** Bolts within `WB_SHELL.draw` m of a rod strike the rod. */
  readonly rodDraw?: boolean;
  /** Knockback is toward the harbour, not away from the boss. */
  readonly harbour?: boolean;
  /** The boss lands this many seconds, stunned, after the move. */
  readonly landStun?: number;
  /** What it leaves on the ground (at each telegraph): fire or smoke for `seconds`, or a boulder as cover. */
  readonly leave?: { readonly kind: 'fire' | 'smoke' | 'boulder'; readonly seconds: number };
  /** Blocking with a full stamina bar takes half. */
  readonly halfIfGuardFull?: boolean;
  /** Not a strike: a script (call the young, lay eggs, mimic, perch volley uses the plain shapes). */
  readonly script?: 'young' | 'eggs' | 'mimic';
}

export const WB_MOVE_MECH: Readonly<Record<string, MoveMech>> = {
  // Ape
  'wb-ape-boulder': { cover: true, leave: { kind: 'boulder', seconds: WB_COVER.seconds } },
  'wb-ape-ledge': { scheduled: true, cover: true, leave: { kind: 'boulder', seconds: WB_COVER.seconds } },
  'wb-ape-mimic': { script: 'mimic' },
  'wb-ape-chest': { script: 'young' },
  // Bird
  'wb-bird-peck': { groundOnly: true },
  'wb-bird-fan': { groundOnly: true, leave: { kind: 'fire', seconds: 8 } },
  'wb-bird-kick': { groundOnly: true },
  'wb-bird-rain': { leave: { kind: 'smoke', seconds: 8 } },
  'wb-bird-sky-burn': { safe: { sites: ['wb-bird:shelter'] }, halfIfGuardFull: true },
  'wb-bird-swoop': { groundOnly: true, leave: { kind: 'fire', seconds: 10 } },
  'wb-bird-dive': { landStun: WB_AIR.down },
  'wb-bird-eggs': { scheduled: true, script: 'eggs' },
  // Turtle
  'wb-turtle-rings': { thunder: true, rings: [8, 16, 24], gap: 0.6, safe: { sites: ['wb-turtle:rock', 'wb-turtle:deck'] } },
  'wb-turtle-bolts': { thunder: true, rodDraw: true },
  'wb-turtle-tide': { harbour: true, safe: { sites: ['wb-turtle:rock', 'wb-turtle:deck'] } },
  'wb-turtle-thousand': { thunder: true, safe: { sites: ['wb-turtle:rock'], marked: true } },
};

// ───────────────────────── monsters of the mechanics ─────────────────────────

/** The small apes, the eggs and the rods. The eggs and rods never act (the system's `monsterDecide` holds them). */
export const WB_ADD_MONSTERS: readonly MonsterDef[] = [
  {
    id: WB_YOUNG.id,
    name: 'ลิงน้อยทงปี้',
    level: 14,
    hp: 600,
    atk: 55,
    def: 12,
    speed: 3.4,
    aggro: 40,
    leash: 60,
    decisionRate: 1.3,
    attack: 'wolf-bite',
    canBlock: false,
    reward: { cultivation: 0, gold: [0, 0] },
    respawn: null,
    sprite: 'wolf',
    scale: 1.2,
  },
  {
    id: WB_EGG.id,
    name: 'ไข่เพลิง',
    level: 20,
    hp: WB_EGG.hp,
    atk: 1,
    def: 10,
    speed: 0,
    aggro: 0,
    leash: 1,
    decisionRate: 99,
    attack: 'wolf-bite',
    canBlock: false,
    reward: { cultivation: 0, gold: [0, 0] },
    respawn: null,
    sprite: 'wolf',
    scale: 1,
  },
  {
    id: WB_SHELL.rodId,
    name: 'ส้อมสายฟ้า',
    level: 24,
    hp: WB_SHELL.rodHp,
    atk: 1,
    def: 10,
    speed: 0,
    aggro: 0,
    leash: 1,
    decisionRate: 99,
    attack: 'wolf-bite',
    canBlock: false,
    reward: { cultivation: 0, gold: [0, 0] },
    respawn: null,
    sprite: 'wolf',
    scale: 1,
  },
];

/** Ids of the inert adds (eggs and rods). */
export const WB_INERT: ReadonlySet<string> = new Set([WB_EGG.id, WB_SHELL.rodId]);
